<template>
  <AppDialog
    class="insights-smart-notifications-dialog"
    append-to-body
    is-dismissable
    :show="show"
    :title="rule.name"
    @close="close"
  >
    <section class="header-description">
      <div>
        <i class="hi hi-bell mr-5 align-baseline"></i>
        {{ createdAt }}
      </div>
      <div class="text-capitalize mt-10">
        <i class="hi hi-info mr-5 align-baseline"></i>
        {{ rule.frequency }}
        {{ ruleSubtitle }}<span v-if="isGeoIpListMatch">:</span>
        <span v-if="rule.metric_type === 'time_spent_in_tool'">
          in 1 or more of the following {{ computedAppURLCopy }}:
        </span>
        <ul>
          <li
            v-for="(item, index) in listHead"
            :key="index"
          >
            {{ item }}
          </li>
        </ul>
      </div>
      <span
        v-if="listRest.length"
        v-tooltip="tooltipOptions"
        :title="tooltipContent"
        class="remaining-tools"
      >
        +{{ listRest.length }} {{ listLabel }}
      </span>
      <div
        v-if="ruleHasDataPrivacy"
        class="mt-10"
      >
        *Excluding users with Data Privacy setting enabled
      </div>
    </section>
    <div class="table-wrapper">
      <AppTable
        :table-data="tableData"
        @sort="sort"
      >
        <AppTableColumn
          v-slot="{ key }"
          cell-classes="text-right p-0"
        >
          {{ key + 1 }}.
        </AppTableColumn>
        <AppTableColumn
          v-slot="{ row }"
          header="Member"
          name="name"
          cell-classes="w-full"
          :sort-by="buildSortOptions('name')"
        >
          <section class="member">
            <AppAvatar
              class="ml-5"
              :avatar="row.avatar"
              size="speck"
            />
            <div class="member-info">
              <div class="d-flex justify-space-between">
                <span>{{ row.name }}</span>
              </div>
            </div>
          </section>
        </AppTableColumn>
        <AppTableColumn
          v-slot="{ row }"
          header="Trigger value"
          name="value"
          header-classes="text-right"
          :sort-by="buildSortOptions('value')"
        >
          <div class="text-right">
            {{ getConditionValue(row.value).value }} {{ getConditionValue(row.value).addon }}
          </div>
        </AppTableColumn>
        <AppTableColumn v-slot="{ row }">
          <a
            v-if="isCountMetric"
            v-tooltip="'View audit log'"
            :href="auditLogUrl(row.user_id)"
            target="_blank"
            class="d-inline-flex align-items-center insights-view-full-day-link gap-5"
            @click="trackAuditLogClick"
          >
            <i class="hi hi-external"></i>
          </a>
          <InsightsViewFullDayLink
            v-else-if="showFullDayLink(rule.frequency)"
            :date="notification.from"
            :organization-id="organization.id"
            :user-id="row.user_id"
          />
        </AppTableColumn>
        <div
          v-if="notification.matches.length >= notification.limit"
          class="header-description mt-10"
        >
          *Smart notifications are limited to {{ notification.limit }} members per alert, consider breaking this
          notification up into smaller groups.
        </div>
      </AppTable>
    </div>
  </AppDialog>
</template>

<script>
import dayjs from 'dayjs';
import { formatDate } from '~/utils/time_utils';
import { SETTINGS_SMART_NOTIFICATIONS } from '~/app_constants';
import { escape, orderBy } from 'lodash-es';
import { mapState } from 'vuex';
import InsightsViewFullDayLink from '~/components/insights/InsightsViewFullDayLink.vue';
import SmartNotificationMixin from '~/mixins/Insights/SmartNotificationMixin';

const APPS_PER_CARD = 5;

export default {
  name: 'InsightsSmartNotificationsDialog',
  components: { InsightsViewFullDayLink },
  mixins: [SmartNotificationMixin],
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    notification: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    loading: false,
    tooltipOptions: Object.freeze({ placement: 'top', html: true }),
    APPS_PER_CARD,
    sortOptions: {
      field: 'name',
      direction: 'asc',
    },
  }),
  computed: {
    ...mapState(['organization']),
    tableData() {
      return orderBy(this.notification.matches, this.sortOptions.field, this.sortOptions.direction);
    },
    rule() {
      return this.notification.snapshot;
    },
    ruleHasDataPrivacy() {
      const privacyMetrics = ['time_spent_in_tool'];

      return privacyMetrics.includes(this.rule.metric_type);
    },
    tooltipContent() {
      // Rendered as raw HTML (tooltip html: true); escape each item — geo-IP `city`
      // entries are arbitrary user-supplied strings, so they must not inject markup.
      const items = this.listRest.map((item) =>
       `<li class="no-wrap">${escape(item)}</li>`
      ).join('');

      return `<ul class="text-left p-20">${items}</ul>`;
    },
    computedMetricType() {
      const metricTypeDict = {
        application: 'time_spent_in_app',
        url: 'time_spent_in_url',
      };

      if (this.rule.metric_type !== 'time_spent_in_tool') {
        return SETTINGS_SMART_NOTIFICATIONS.insights.metricType[this.rule.frequency][this.rule.metric_type];
      }

      if (this.rule.data && this.rule.data.tools) {
        return SETTINGS_SMART_NOTIFICATIONS.insights.metricType[this.rule.frequency][
          metricTypeDict[this.rule.data.tools.type]
        ];
      }

      return '';
    },
    computedAppURLCopy() {
      return this.rule.data.tools.type === 'application' ? 'Apps' : 'URLs';
    },
    hasTools() {
      return !!this.rule?.data?.tools;
    },
    createdAt() {
      return formatDate(dayjs(this.notification.created_at));
    },
    tools() {
      if (this.hasTools) {
        const tools = Object.values(this.rule.data.tools.ids) || [];
        const names = this.rule.data.tools.names || [];

        return [...tools, ...names]
      }

      return [];
    },
    // List rendered under the subtitle: tool names for time_spent_in_tool,
    // or the IP/city/country list for geo_ip_list_match.
    listItems() {
      if (this.rule.metric_type === 'time_spent_in_tool') {
        return this.tools;
      }

      if (this.isGeoIpListMatch) {
        return this.geoIpList;
      }

      return [];
    },
    listHead() {
      return this.listItems.slice(0, APPS_PER_CARD)
    },
    listRest() {
      return this.listItems.slice(APPS_PER_CARD)
    },
    listLabel() {
      if (this.rule.metric_type === 'time_spent_in_tool') {
        return this.computedAppURLCopy;
      }

      if (this.isGeoIpListMatch) {
        return this.geoIpScopeLabel;
      }

      return '';
    }
  },
  methods: {
    close() {
      this.$emit('close');
    },
    sort({ field, direction }) {
      this.sortOptions = { field, direction };
    },
    buildSortOptions(field) {
      return {
        field,
        direction: this.sortOptions.field === field ? this.sortOptions.direction : null,
      };
    },
  },
};
</script>
